import { useApi } from "api/ApiProvider";
import Api from "axiosApi/api";
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider";
import { useState } from "react";
import intl from 'components/i18n/ReactIntlWrapper';
import { CreateEmployeeCommand } from "axiosApi/models";
import { handleAPIError } from "common/errorHandler";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import ErrorSummary from "components/common/ErrorSummary";
import { AddCircle, Save } from "@mui/icons-material";
import RequiredField from "components/common/RequiredField";
import Spinner from "components/Spinner";

const DEFAULT_NEW_EMPLOYEE = {
    name: "",
    lastName: "",
    emailAddress: ""
}

const ModalNewEmployee = () => {
    const api: Api = useApi();
    const toast = useToastMessageQueue();
    const [loadingSave, setLoadingSave] = useState(false);
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState(DEFAULT_NEW_EMPLOYEE);
    const [disabled, setDisabled] = useState(true);
    const [errors, setErrors] = useState({name: null, lastName: null, emailAddress: null});

    const closeModal = () => {
        setOpen(false);
        setInput(DEFAULT_NEW_EMPLOYEE);
        setErrors({name: null, lastName: null, emailAddress: null});
        setDisabled(true);
    }

    const handleInputChange = (e) => {
        setInput({
            ...input,
            [e.target.name]: e.target.value
        });
        setErrors(validate({
            ...input,
            [e.target.name]: e.target.value
        }));
    };

    const validate = function (input) {
        setDisabled(false);
        let errors: any = {name: null, lastName: null, emailAddress: null};
        if (!input.name) {
            errors.name = intl.get('validate.errors.isRequired');
            setDisabled(true);
        }
        if (!input.lastName) {
            errors.lastName = intl.get('validate.errors.isRequired');
            setDisabled(true);
        }
        if (!input.emailAddress) {
            errors.emailAddress = intl.get('validate.errors.isRequired');
            setDisabled(true);
        } else if (!/\S+@\S+\.\S+/.test(input.emailAddress)) {
            errors.emailAddress = intl.get('validate.errors.isInvalid');
            setDisabled(true);
        }
        return errors;
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoadingSave(true);
        const errors = validate(input);
        setErrors(errors);
        if (errors.name === null && errors.lastName === null && errors.emailAddress === null) {
            let cmd: CreateEmployeeCommand = {
                name: input.name,
                lastName: input.lastName,
                emailAddress: input.emailAddress
            };
            try {
                await api.employeeApi.apiVversionEmployeePost("1", cmd, {});
                toast.success({ message: intl.get('employeeDetail.toast.success.handleSubmit.add')});
                closeModal();
            } catch (error) {
                handleAPIError(error, toast, errors);
                setErrors({ ...errors });
            } finally {
                setLoadingSave(false);
            }
        }
    };

    const handleCancel = () => {
        setInput(DEFAULT_NEW_EMPLOYEE);
        setErrors({name: null, lastName: null, emailAddress: null});
        closeModal();
    };

    return (<>
        <Button
            variant='text'
            onClick={() => setOpen(true)}
            startIcon={<AddCircle />}
        >
            {intl.get('newUser.newEmployee')}
        </Button>
        <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm">
            <DialogTitle>
                <Typography variant="h2">{intl.get('newEmployee.header')}</Typography>
                <ErrorSummary errors={errors} showEverything={false} mode="warning"></ErrorSummary>
            </DialogTitle>
            <DialogContent>
                <Stack py={2} gap={2}>
                    <TextField
                        id="employeeName"
                        name="name"
                        placeholder={intl.get('employeeForm.placeholder.name')}
                        value={input?.name}
                        onChange={handleInputChange}
                        fullWidth
                        label={intl.get('employeeForm.name')}
                        error={!!errors.name}
                        helperText={errors.name}                               
                        />
                    <TextField
                        id="employeeLastName"
                        name="lastName"
                        placeholder={intl.get('employeeForm.placeholder.tradeName')}
                        value={input?.lastName}
                        onChange={handleInputChange}
                        fullWidth
                        label={intl.get('employeeForm.tradeName')}
                        error={!!errors.lastName}
                        helperText={errors.lastName}                               
                        />
                    <TextField
                        id="employeeEmail"
                        name="emailAddress"
                        placeholder={intl.get('employeeForm.placeholder.email')}
                        value={input?.emailAddress}
                        onChange={handleInputChange}
                        fullWidth
                        label={intl.get('employeeForm.email')}
                        error={!!errors.emailAddress}
                        helperText={errors.emailAddress}
                        />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Box display={"flex"} justifyContent="space-between" width="100%">
                    <RequiredField/>
                        { loadingSave ? <Spinner small={true} /> : (
                    <Box display={"flex"} gap={2}>
                        <Button variant="outlined" color="secondary" onClick={handleCancel}>
                            {intl.get('cancel.button')}
                        </Button>
                        <Button variant="contained" color="primary" disabled={disabled} onClick={handleSave} startIcon={<Save/>}>
                            {intl.get('save.button')}
                        </Button>
                    </Box>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    </>)
}

export default ModalNewEmployee;